import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, SpaceBetween } from '@amzn/awsui-components-react';
import CONSTANTS from 'src/utils/constants';
import ErrorUtils from 'src/utils/ErrorUtils';
import useReducerWithLogger from 'src/services/utils/ReducerWithLogger';
import { initialState } from 'src/services/tp-allocation/totals/WorksheetTotalsState';
import { worksheetTotalsReducer, ACTIONS } from 'src/services/tp-allocation/totals/WorksheetTotalsReducer';
import { TPELoadingSpinner } from 'src/components/shared/TPELoadingSpinner';
import { SavingStatus } from 'src/models/common/SavingStatus';
import WorksheetTotal from 'src/models/tp-allocation/WorksheetTotal';
import { DerivedAllocationSheetBuilderContext } from 'src/services/derived-allocation-sheet/DerivedAllocationSheetBuilderContext';
import { ACTIONS as DAS_ACTIONS } from 'src/services/derived-allocation-sheet/DerivedAllocationSheetBuilderReducer';
import {
    newDasTotal, toCreateDASTotalRequest, toSavedWorksheetTotal, toUpdateDASTotalRequest, toWorksheetTotals,
} from 'src/services/derived-allocation-sheet/dasTotalMapper';
import { DASTotal } from 'src/models/derived-allocation-sheet/DASTotal';
import { DasTotalsContext } from 'src/services/derived-allocation-sheet/DasTotalsContext';
import { validateDasTotal } from 'src/services/derived-allocation-sheet/dasTotalChecks';
import DasTotalsGrid from './DasTotalsGrid';
import DasExpressionLegend from './DasExpressionLegend';
import DasStepInfo from './DasStepInfo';

const isUnsaved = (t: WorksheetTotal) => t.isNew || t.savingStatus === SavingStatus.Unsaved;

/** TPA naming for copies: `DUPLICATE - A`, then `DUPLICATE(2) - A`, and so on. */
const copyNameOf = (original: WorksheetTotal, totals: WorksheetTotal[]) => {
    const count = totals.filter((t) => (t.description || '').endsWith(original.description)).length;
    return `DUPLICATE${count === 1 ? '' : `(${count})`} - ${original.description}`;
};

/**
 * Step 2 (Configure totals) of the derived allocation sheet builder, adapted from the TPA totals container;
 * keep the two in sync. It reuses the TPA totals reducer. Totals load, save and delete through the derived
 * allocation sheet service, each total is checked before it is sent, and every write refreshes the sheet
 * header. Saves run one at a time: an edit that arrives while a save is in flight waits for it, so a new row
 * is created once and then updated. After a rename here or in Step 1 the totals are reread once none is unsaved,
 * so the references the backend rewrote show.
 */
export default function DasTotalsSection(props: { onNextStep?: () => void }) {
    const { onNextStep } = props;
    const { dasState, dasDispatch, services, sheetId } = useContext(DerivedAllocationSheetBuilderContext);
    const { viewMode, dataSources, totalsRefreshToken } = dasState;
    // The builder renders the steps only once a sheet is loaded.
    const sheetVersion = dasState.sheet!.sheetVersion;
    const [totalsState, totalsDispatch] = useReducerWithLogger(worksheetTotalsReducer, initialState);
    const { totals, updateTotalPayload } = totalsState;
    const editable = viewMode === CONSTANTS.VIEW_MODE.EDITABLE;

    // A Step 1 rename bumps the refresh token; the totals are reread only when no total is unsaved.
    const [totalsQuery, setTotalsQuery] = useState(`fetch-${totalsRefreshToken}`);
    const [totalsResult, totalsLoading, loadError] =
        services.derivedAllocationSheetService.getDASTotals(totalsQuery, { sheetId, version: sheetVersion });
    const [loaded, setLoaded] = useState(false);
    const loadFailed = !!loadError;
    const loading = totalsLoading || (!loaded && !loadFailed);
    const [containerLevelError, setContainerLevelError] = useState(undefined as string | undefined);
    const [unsavedTotals, setUnsavedTotals] = useState(undefined as WorksheetTotal[] | undefined);
    const [isSaving, setIsSaving] = useState(false);
    const mountedRef = useRef(true);
    useEffect(() => () => { mountedRef.current = false; }, []);
    const inFlight = useRef(false);
    // Edits that arrived during a save, by row id, and the ids new rows got when they were created.
    const waiting = useRef(new Map<string, WorksheetTotal>());
    const createdIds = useRef(new Map<string, string>());
    const totalsRef = useRef(totals);
    totalsRef.current = totals;
    // Saved name of each total, to tell a rename from other edits.
    const savedNames = useRef(new Map<string, string>());
    const reloadPending = useRef(false);
    const reloadCount = useRef(0);
    const [reloadRequest, setReloadRequest] = useState(0);
    const requestReload = () => {
        reloadPending.current = true;
        setReloadRequest((n) => n + 1);
    };

    const savedDataSources = useMemo(() => dataSources.filter((d) => !d.isNewRecord && !!d.description), [dataSources]);
    const dataSourceNames = useMemo(() => savedDataSources.map((d) => d.description), [savedDataSources]);

    useEffect(() => {
        if (totalsQuery === `fetch-${totalsRefreshToken}`) {
            return;
        }
        if (totalsRef.current.some(isUnsaved)) {
            setContainerLevelError('Step 1 names changed. Save or remove the unsaved totals to reload them.');
            requestReload();
            return;
        }
        setTotalsQuery(`fetch-${totalsRefreshToken}`);
    }, [totalsRefreshToken]);

    useEffect(() => {
        if (!reloadPending.current || isSaving || waiting.current.size > 0 || totals.some(isUnsaved)) {
            return;
        }
        reloadPending.current = false;
        reloadCount.current += 1;
        setTotalsQuery(`fetch-${totalsRefreshToken}-reload-${reloadCount.current}`);
    }, [reloadRequest, isSaving, totals]);

    useEffect(() => {
        if (totalsResult == null) {
            return;
        }
        setContainerLevelError(undefined);
        const loadedTotals = toWorksheetTotals(totalsResult.totals);
        savedNames.current = new Map(loadedTotals.map((t) => [t.totalId, t.description]));
        totalsDispatch(ACTIONS.SET_WORKSHEET_TOTALS.withPayload(loadedTotals));
        setLoaded(true);
    }, [totalsResult]);

    // Step 3 reads the totals from the builder state.
    useEffect(() => {
        dasDispatch(DAS_ACTIONS.SET_TOTALS.withPayload(totals));
    }, [totals]);

    const stopSaving = (message: string) => {
        setContainerLevelError(message);
        setUnsavedTotals(undefined);
        waiting.current.clear();
        totalsDispatch(ACTIONS.SET_SAVE_TOTAL_PAYLOAD.withPayload(null));
    };

    // Save one total: the reducer queues it on edit (blur) and Save drains every unsaved total through here.
    useEffect(() => {
        if (updateTotalPayload == null) {
            return;
        }
        const queued = updateTotalPayload as WorksheetTotal;
        if (inFlight.current) {
            waiting.current.set(queued.totalId, queued);
            return;
        }
        const createdId = createdIds.current.get(queued.totalId);
        const payload = createdId ? { ...queued, totalId: createdId, isNew: false } : queued;
        const current = totals.find((t) => t.totalId === payload.totalId) || payload;
        const candidate = { ...current, ...payload };
        const listed = totals.map((t) => (t.totalId === candidate.totalId ? candidate : t));
        const parts = services.readOnlyCalculationService.breakExpressionIntoParts(candidate.expression,
            listed.map((t) => ({ name: t.description, visibleSequence: t.visibleSequence })),
            dataSourceNames.map((name) => ({ name })), new Set());
        const problem = validateDasTotal(candidate, listed, dataSourceNames, parts);
        if (problem) {
            stopSaving(problem);
            return;
        }
        inFlight.current = true;
        setIsSaving(true);
        const sent: WorksheetTotal = { ...payload, expressionTokens: parts };
        const service = services.derivedAllocationSheetService;
        const request: Promise<{ total: DASTotal | null }> = sent.isNew
            ? service.createDASTotal(toCreateDASTotalRequest(sheetId, sent))
            : service.updateDASTotal(toUpdateDASTotalRequest(sheetId, sent));
        request
            .then(({ total }) => {
                // Released before any dispatch below, so the next queued edit is sent rather than queued again.
                inFlight.current = false;
                if (!mountedRef.current) return;
                const saved = toSavedWorksheetTotal(total, sent);
                if (payload.isNew) {
                    createdIds.current.set(payload.totalId, saved.totalId);
                } else if (savedNames.current.get(saved.totalId) !== saved.description) {
                    requestReload();
                }
                savedNames.current.set(saved.totalId, saved.description);
                setContainerLevelError(undefined);
                totalsDispatch(ACTIONS.REFRESH_WORKSHEET_TOTAL.withPayload(saved));
                dasDispatch(DAS_ACTIONS.REFRESH_SHEET);
                setUnsavedTotals((queue) => (queue == null ? queue
                    : queue.filter((t) => t.totalId !== queued.totalId && t.totalId !== saved.totalId)));
                const next = waiting.current.values().next();
                if (!next.done) {
                    waiting.current.delete(next.value.totalId);
                    totalsDispatch(ACTIONS.SET_SAVE_TOTAL_PAYLOAD.withPayload(next.value));
                }
            })
            .catch((ex) => {
                inFlight.current = false;
                if (mountedRef.current) stopSaving(ErrorUtils.getMessage(ex));
            })
            .finally(() => {
                // A chained save may already be in flight; it clears the flag when it settles.
                if (mountedRef.current && !inFlight.current) setIsSaving(false);
            });
    }, [updateTotalPayload]);

    // Drain the Save queue one total at a time, in order.
    useEffect(() => {
        if (unsavedTotals == null || unsavedTotals.length === 0 || isSaving || updateTotalPayload != null) {
            if (unsavedTotals != null && unsavedTotals.length === 0) setUnsavedTotals(undefined);
            return;
        }
        totalsDispatch(ACTIONS.SET_SAVE_TOTAL_PAYLOAD.withPayload(unsavedTotals[0]));
    }, [unsavedTotals, isSaving, updateTotalPayload]);

    const save = () => {
        totalsDispatch(ACTIONS.SET_TOTAL_BEING_EDITED.withPayload(null));
        setContainerLevelError(undefined);
        const pending = totals.filter(isUnsaved);
        if (pending.length > 0) {
            setUnsavedTotals(pending);
        }
    };

    const addTotal = () => {
        totalsDispatch(ACTIONS.ADD_WORKSHEET_TOTAL.withPayload(newDasTotal(totals.length + 1, sheetId)));
    };

    // A copy is appended rather than inserted after the original, because create adds the total at the end of
    // the stored order. It is saved right away, as TPA does.
    const duplicate = (total: WorksheetTotal) => {
        const copy = {
            ...newDasTotal(totals.length + 1, sheetId),
            description: copyNameOf(total, totals),
            expression: total.expression,
            expressionTokens: total.expressionTokens,
            savingStatus: SavingStatus.Unsaved,
        };
        totalsDispatch(ACTIONS.ADD_WORKSHEET_TOTAL.withPayload(copy));
        totalsDispatch(ACTIONS.SET_SAVE_TOTAL_PAYLOAD.withPayload(copy));
    };

    const atLeastOneSaved = totals.some((t) => !t.isNew);
    const saving = isSaving || (unsavedTotals != null && unsavedTotals.length > 0);
    const hasUnsavedWork = saving || totals.some(isUnsaved);

    // The builder keeps another version from opening while Step 2 has work that is not saved.
    useEffect(() => {
        dasDispatch(DAS_ACTIONS.SET_STEP_UNSAVED.withPayload({ step: 2, unsaved: hasUnsavedWork, saving }));
    }, [hasUnsavedWork, saving]);
    useEffect(() => () => dasDispatch(DAS_ACTIONS.SET_STEP_UNSAVED.withPayload({ step: 2, unsaved: false })), []);
    const blocked = loading || loadFailed;

    const contextValue = useMemo(() => ({
        services, totalsState, totalsDispatch, dataSources: savedDataSources, viewMode, sheetId,
    }), [totalsState, totalsDispatch, savedDataSources, viewMode, sheetId]);

    return (
        <DasTotalsContext.Provider value={contextValue}>
            <div className="wizardBoxContentContainer" data-class="dasStep2">
                <SpaceBetween direction="vertical" size="s">
                    <DasStepInfo>
                        Named denominators referenced by Step 3. Reference Step-1 rows by their description; totals can
                        reference the totals above them.
                    </DasStepInfo>
                    <div className="totalsHeader">
                        <TPELoadingSpinner loading={loading} loadingText="Loading totals">
                            <SpaceBetween direction="vertical" size="l">
                                {loaded && (
                                    <DasTotalsGrid
                                        onTotalsLevelError={setContainerLevelError}
                                        onTotalRemoved={() => dasDispatch(DAS_ACTIONS.REFRESH_SHEET)}
                                        onDuplicate={duplicate}
                                        saving={saving}
                                    />
                                )}
                                {(containerLevelError || loadError) && (
                                    <Alert type="error" header="Totals Error" statusIconAriaLabel="Error"
                                           action={loadError
                                               ? <Button data-class="retryTotalsButton" onClick={requestReload}>Retry</Button>
                                               : undefined}>
                                        {containerLevelError || loadError}
                                    </Alert>
                                )}
                            </SpaceBetween>
                        </TPELoadingSpinner>
                    </div>
                    <DasExpressionLegend />
                </SpaceBetween>
                {editable &&
                    <div className="actionButtonsContainer">
                        <Box float="left">
                            <Button data-class="subPrimaryButton" onClick={addTotal} disabled={blocked || saving}>Add total</Button>
                        </Box>
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="m">
                                <Button data-class="saveButton" onClick={save} loading={saving} disabled={blocked}>
                                    {saving ? 'Saving...' : 'Save'}
                                </Button>
                                <Button data-class="nextButton" variant="primary" disabled={!atLeastOneSaved || blocked}
                                        onClick={() => onNextStep?.()}>Next Step</Button>
                            </SpaceBetween>
                        </Box>
                    </div>
                }
            </div>
        </DasTotalsContext.Provider>
    );
}
